import styles from "./employment_section.css";
import { Body, links as typographyLinks } from "../typography";
import { TextInput } from "../text_input";
import { useBoardConfiguration } from "~/hooks/use_board_configuration";
import { useUpdateEffect } from "react-use";
import { Select, type Option } from "../select";
import { Checkbox } from "../checkbox";
import TrashIcon from "../icons/trash";
import { IconButton } from "../icon_button";
import type { EmploymentAnswers } from "~/utils/build_application";
import { useTranslation } from "react-i18next";
import { useMemo, useState, useEffect } from "react";
import { focusLastSection } from "~/utils/focus_last_section";
import { Resizer } from "~/utils/resizer";

export const links = () => [{ rel: "stylesheet", href: styles }, ...typographyLinks()];

interface Props {
  employments: EmploymentAnswers;
  required: boolean;
  internal?: boolean;
  errors: Record<string, string>[];
  onChange: (employments: EmploymentAnswers) => void;
}

export const EmploymentSection = ({ employments, required, internal, errors, onChange }: Props) => {
  const { outside_label } = useBoardConfiguration();
  const { t } = useTranslation("job_post");

  const [shouldFocusSection, setShouldFocusSection] = useState<boolean>(false);

  const months = useMemo(() => {
    return ([
    { label: t("months.january"), value: 1 },
    { label: t("months.february"), value: 2 },
    { label: t("months.march"), value: 3 },
    { label: t("months.april"), value: 4 },
    { label: t("months.may"), value: 5 },
    { label: t("months.june"), value: 6 },
    { label: t("months.july"), value: 7 },
    { label: t("months.august"), value: 8 },
    { label: t("months.september"), value: 9 },
    { label: t("months.october"), value: 10 },
    { label: t("months.november"), value: 11 },
    { label: t("months.december"), value: 12 }] as
    Option[]);
  }, [t]);

  useUpdateEffect(() => {
    Resizer.getInstance().handleResize();
  }, [employments.length]);

  useEffect(() => {
    if (shouldFocusSection) {
      focusLastSection(".employment-form");
      setShouldFocusSection(false);
    }
  }, [shouldFocusSection]);

  const updateEmploymentValue = (
  index: number,
  key:
  "companyName" |
  "title" |
  "startDateMonth" |
  "startDateYear" |
  "endDateMonth" |
  "endDateYear" |
  "currentRole",
  value: any) =>
  {
    const newEmployments = [...employments];
    newEmployments[index][key] = value;
    onChange(newEmployments);
  };

  const fields = () => {
    return employments.map((employment, index) => {
      const error = errors[index];
      return (
        <div key={employment.key} className="employment-form">
          <hr />
          <div className="header-row">
            <Body medium>{t("employment.employment")}</Body>
            {employments.length > 1 &&
            <IconButton
              label="Remove employment"
              icon={TrashIcon}
              onClick={() => {
                onChange(employments.filter((e) => e.key !== employment.key));
              }}>
            </IconButton>}

          </div>
          <TextInput
            id={"company-name"}
            required={required}
            label={t("employment.company_name")}
            maxLength={255}
            onChange={(event) => {
              updateEmploymentValue(index, "companyName", event.currentTarget.value);
            }}
            outsideLabel={outside_label}
            error={error?.company_name} />

          <TextInput
            id={"title"}
            required={required}
            label={t("employment.title")}
            maxLength={255}
            onChange={(event) => {
              updateEmploymentValue(index, "title", event.currentTarget.value);
            }}
            outsideLabel={outside_label}
            error={error?.title} />

          <div className="date-row">
            <Select
              id={"start-date-month"}
              label={t("employment.start_date_month")}
              required={required}
              options={months}
              onSelect={(option) => {
                updateEmploymentValue(index, "startDateMonth", (option as Option)?.value);
              }}
              isClearable
              outsideLabel={outside_label}
              error={error?.start_date_month} />

            <TextInput
              id={"start-date-year"}
              required={required}
              label={t("employment.start_date_year")}
              maxLength={4}
              onChange={(event) => {
                updateEmploymentValue(index, "startDateYear", event.currentTarget.value);
              }}
              outsideLabel={outside_label}
              error={error?.start_date_year} />

          </div>
          <div className="date-row">
            <Select
              id={"end-date-month"}
              label={t("employment.end_date_month")}
              required={required}
              options={months}
              onSelect={(option) => {
                updateEmploymentValue(index, "endDateMonth", (option as Option)?.value);
              }}
              isClearable
              outsideLabel={outside_label}
              isDisabled={employment.currentRole}
              error={error?.end_date_month} />

            <TextInput
              id={"end-date-year"}
              required={required}
              label={t("employment.end_date_year")}
              maxLength={4}
              onChange={(event) => {
                updateEmploymentValue(index, "endDateYear", event.currentTarget.value);
              }}
              outsideLabel={outside_label}
              disabled={employment.currentRole}
              error={error?.end_date_year} />

            <Checkbox
              isMulti
              internal={internal}
              id={"current-role"}
              options={[
              {
                value: "1",
                label: t("employment.current_role")
              }]}

              onSelect={(selected) => {
                updateEmploymentValue(index, "currentRole", (selected as Option[]).length > 0);
              }} />

          </div>
        </div>);

    });
  };

  return (
    <div className="employment--container">
      {fields()}

      <button
        className="add-another-button"
        type="button"
        onClick={() => {
          const lastKey = (employments[employments.length - 1].key as number);
          onChange([...employments, { key: lastKey + 1 }]);
          setShouldFocusSection(true);
        }}>

        {t("employment.add_another")}
      </button>
    </div>);

};